/** Hooks for Cylindo 3D/AR availability checks */

import {useEffect, useState} from 'react';

import {
  buildCylindoArqrUrl,
  extractCylindoParams,
  normalizeCylindoCode,
} from '@amblique/king-living/app/utils/cylindo-params';

export {extractCylindoParams};

// Cylindo `/configuration` HEAD check timeout. Was 1s "fast UX" but it caused
// the gallery to flip to "Image not available" when the network was slow or
// the variant config was cold-cached. The check is fire-and-forget; loading
// state is masked by the ImageGallery skeleton, so a longer timeout has no UX
// downside while genuine 4xx/5xx responses still short-circuit immediately.
const AVAILABILITY_CHECK_TIMEOUT_MS = 10_000;

/** Fetch with timeout */
const fetchWithTimeout = async (url, signal, timeoutMs) => {
  const timeoutPromise = new Promise((_, reject) => setTimeout(
    () => reject(new Error('Cylindo availability check timeout')),
    timeoutMs
  ));

  return Promise.race([fetch(url, {signal}), timeoutPromise]);
};

/** Check Cylindo 3D/AR availability for product */
export const useCylindoAvailability = (product, imageGroups = [], imageSource = 'cylindo', heroImageGroup = null) => {
  const [availability, setAvailability] = useState({
    has3D: false,
    hasAR: false,
    cylindoParams: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    if (!product || imageSource !== 'cylindo') {
      setAvailability({
        has3D: false,
        hasAR: false,
        cylindoParams: null,
        isLoading: false,
        error: null,
      });

      return;
    }

    const params = extractCylindoParams(product, imageGroups, heroImageGroup);

    if (!params) {
      setAvailability({
        has3D: false,
        hasAR: false,
        cylindoParams: null,
        isLoading: false,
        error: 'Not a Cylindo product',
      });

      return;
    }

    setAvailability((prev) => ({...prev, isLoading: true}));

    const controller = new AbortController();

    const checkAvailability = async () => {
      try {
        // Normalize first so a pre-encoded productCode (e.g. spaces stored as
        // `%20`) is decoded once before re-encoding — otherwise the path
        // ends up double-encoded (`%25...`) and Cylindo returns 404.
        const configUrl = `https://content.cylindo.com/api/v2/${params.customerId}/products/${encodeURIComponent(normalizeCylindoCode(params.productCode))}/configuration`;
        const response = await fetchWithTimeout(configUrl, controller.signal, AVAILABILITY_CHECK_TIMEOUT_MS);

        if (response.ok) {
          await response.json();

          const arqrUrl = buildCylindoArqrUrl(params.productCode, params.customerId, params.configuration);

          setAvailability({
            has3D: true,
            hasAR: true,
            cylindoParams: {...params, arqrUrl},
            isLoading: false,
            error: null,
          });
        } else if (response.status === 404) {
          const arqrUrl = buildCylindoArqrUrl(params.productCode, params.customerId, params.configuration);

          setAvailability({
            has3D: false,
            hasAR: true,
            cylindoParams: {...params, arqrUrl},
            isLoading: false,
            error: null,
          });
        } else {
          throw new Error(`Configuration check failed: ${response.status}`);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        setAvailability({
          has3D: false,
          hasAR: false,
          cylindoParams: null,
          isLoading: false,
          error: error.message,
        });
      }
    };

    checkAvailability();

    return () => controller.abort();
  }, [product, imageGroups, imageSource, heroImageGroup]);

  return availability;
};

/** Check Cylindo AR availability (lightweight) */
export const useCylindoAR = (cylindoId, customerId, configuration) => {
  const [state, setState] = useState({arqrUrl: null, isChecking: false});

  useEffect(() => {
    if (!cylindoId || !customerId) {
      setState({arqrUrl: null, isChecking: false});

      return;
    }

    setState((prev) => ({...prev, isChecking: true}));

    const controller = new AbortController();

    const check = async () => {
      try {
        const configUrl = `https://content.cylindo.com/api/v2/${customerId}/products/${encodeURIComponent(normalizeCylindoCode(cylindoId))}/configuration`;
        const response = await fetchWithTimeout(configUrl, controller.signal, AVAILABILITY_CHECK_TIMEOUT_MS);

        if (response.ok || response.status === 404) {
          const arqrUrl = buildCylindoArqrUrl(cylindoId, customerId, configuration);

          setState({arqrUrl, isChecking: false});
        } else {
          setState({arqrUrl: null, isChecking: false});
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        setState({arqrUrl: null, isChecking: false});
      }
    };

    check();

    return () => controller.abort();
  }, [cylindoId, customerId, configuration]);

  return state;
};
